/**
 * Import Runs History Page
 * Phase 7: View import run history and staging details
 */

import { useState } from 'react';
import { TopBar } from '@/components/layout/TopBar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useImportRuns, useImportRunDetails, useImportSources } from '@/hooks/useImportSources';
import {
  FileSpreadsheet,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  AlertTriangle,
  Eye,
  ArrowLeft,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { useNavigate, useSearchParams } from 'react-router-dom';

const STATUS_CONFIG: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ReactNode }> = {
  pending: { label: 'Pending', variant: 'secondary', icon: <Clock className="h-3 w-3" /> },
  running: { label: 'Running', variant: 'default', icon: <Loader2 className="h-3 w-3 animate-spin" /> },
  completed: { label: 'Completed', variant: 'secondary', icon: <CheckCircle2 className="h-3 w-3 text-green-500" /> },
  failed: { label: 'Failed', variant: 'destructive', icon: <XCircle className="h-3 w-3" /> },
  partial: { label: 'Partial', variant: 'outline', icon: <AlertTriangle className="h-3 w-3 text-yellow-500" /> },
};

const STAGING_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'text-muted-foreground' },
  valid: { label: 'Valid', color: 'text-blue-500' },
  duplicate: { label: 'Duplicate', color: 'text-yellow-500' },
  invalid: { label: 'Invalid', color: 'text-red-500' },
  imported: { label: 'Imported', color: 'text-green-500' },
  skipped: { label: 'Skipped', color: 'text-muted-foreground' },
};

export default function ImportRuns() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sourceFilter = searchParams.get('source') || undefined;

  const { data: sources } = useImportSources();
  const { data: runs, isLoading } = useImportRuns(sourceFilter);

  const [selectedRunId, setSelectedRunId] = useState<string | undefined>();
  const { data: runDetails, isLoading: loadingDetails } = useImportRunDetails(selectedRunId);

  const handleSourceFilterChange = (value: string) => {
    if (value === 'all') {
      navigate('/import-runs');
    } else {
      navigate(`/import-runs?source=${value}`);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <TopBar />

      <div className="container max-w-6xl mx-auto py-6 px-4 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Import Run History</h1>
            <p className="text-muted-foreground">
              View the history of automated import runs
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Select value={sourceFilter || 'all'} onValueChange={handleSourceFilterChange}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                {sources?.map((source) => (
                  <SelectItem key={source.id} value={source.id}>
                    {source.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => navigate('/import-sources')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Sources
            </Button>
          </div>
        </div>

        {/* Runs Table */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Runs</CardTitle>
            <CardDescription>
              Showing the last 100 import runs
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : !runs?.length ? (
              <div className="text-center py-12">
                <FileSpreadsheet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No import runs found</p>
              </div>
            ) : (
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Source</TableHead>
                      <TableHead>File</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Rows</TableHead>
                      <TableHead>Imported</TableHead>
                      <TableHead>Skipped</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {runs.map((run) => {
                      const statusConfig = STATUS_CONFIG[run.status] || STATUS_CONFIG.pending;
                      return (
                        <TableRow key={run.id}>
                          <TableCell className="font-medium">
                            {run.import_sources?.name || 'Unknown'}
                          </TableCell>
                        <TableCell className="text-muted-foreground max-w-[200px] truncate">
                            {run.file_name || '—'}
                          </TableCell>
                          <TableCell>
                            <Badge variant={statusConfig.variant} className="gap-1">
                              {statusConfig.icon}
                              {statusConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell>{run.rows_total ?? '—'}</TableCell>
                          <TableCell className="text-green-600">{run.rows_imported ?? '—'}</TableCell>
                          <TableCell className="text-muted-foreground">{run.rows_skipped ?? '—'}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {run.created_at
                              ? formatDistanceToNow(new Date(run.created_at), { addSuffix: true })
                              : '—'}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setSelectedRunId(run.id)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Run Details Dialog */}
      <Dialog open={!!selectedRunId} onOpenChange={() => setSelectedRunId(undefined)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Import Run Details</DialogTitle>
            <DialogDescription>
              {runDetails?.file_name || 'Loading...'}
            </DialogDescription>
          </DialogHeader>

          {loadingDetails ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : runDetails ? (
            <div className="space-y-4 flex-1 overflow-hidden">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold">{runDetails.rows_total ?? 0}</div>
                    <div className="text-xs text-muted-foreground">Total Rows</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-green-600">{runDetails.rows_imported ?? 0}</div>
                    <div className="text-xs text-muted-foreground">Imported</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-yellow-600">{runDetails.rows_skipped ?? 0}</div>
                    <div className="text-xs text-muted-foreground">Skipped</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-red-600">{runDetails.rows_failed ?? 0}</div>
                    <div className="text-xs text-muted-foreground">Errors</div>
                  </CardContent>
                </Card>
              </div>

              {/* Error message if any */}
              {runDetails.error_message && (
                <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                  <strong>Error:</strong> {runDetails.error_message}
                </div>
              )}

              {/* Staging rows */}
              <div className="flex-1 overflow-hidden">
                <h4 className="font-medium mb-2">Staging Rows</h4>
                <ScrollArea className="h-[300px] border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">#</TableHead>
                        <TableHead>External ID</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Ticket ID</TableHead>
                        <TableHead>Validation</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {runDetails.staging_rows?.map((row: any) => {
                        const statusConfig = STAGING_STATUS_CONFIG[row.status] || STAGING_STATUS_CONFIG.pending;
                        return (
                          <TableRow key={row.id}>
                            <TableCell className="text-muted-foreground">{row.row_number}</TableCell>
                            <TableCell className="font-mono text-sm">{row.external_id || '—'}</TableCell>
                            <TableCell>
                              <span className={statusConfig.color}>{statusConfig.label}</span>
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              {row.created_ticket_id || row.duplicate_of_ticket_id || '—'}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                              {row.validation_errors
                                ? JSON.stringify(row.validation_errors)
                                : '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </div>

              {/* Timestamps */}
              <div className="text-xs text-muted-foreground">
                Started: {runDetails.started_at ? format(new Date(runDetails.started_at), 'PPpp') : 'Not started'}
                {runDetails.completed_at && (
                  <> • Finished: {format(new Date(runDetails.completed_at), 'PPpp')}</>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
